import { Segment } from "./segment";
import { Pt, pt, ptEquals } from "./pt";

class IntersectionPoint {
  segment: Segment;
  pos: number;
  pt: Pt;

  constructor(segment: Segment, pos: number, pt: Pt) {
    this.segment = segment;
    this.pos = pos;
    this.pt = pt;
  }
}

function intersections(
  segment1: Segment,
  segment2: Segment
): IntersectionPoint[] {
  let av = sub(segment1.end, segment1.start);
  let bv = sub(segment2.end, segment2.start);
  let d = cross(av, bv);

  if (Math.abs(d) <= 1e-6) {
    const coll = collinearIntersections(segment1, segment2);
    if (coll) return coll;
    else if (Math.abs(d) == 0) return [];
  }

  let asb = sub(segment1.start, segment2.start);
  let s = cross(bv, asb) / d;
  let t = cross(av, asb) / d;
  if (s >= 0 && s <= 1 && t >= 0 && t <= 1) {
    let pt = segment1.position(s);
    return [
      new IntersectionPoint(segment1, s, pt),
      new IntersectionPoint(segment2, t, pt),
    ];
  } else {
    return [];
  }
}

function collinearIntersections(
  segment1: Segment,
  segment2: Segment
): IntersectionPoint[] {
  let result = [];
  for (let i = 0; i < 2; i++) {
    let tp = segment1.position(i);
    let tb = nearestPoint(segment2, tp);
    if (tb <= 0) {
      let s = nearestPoint(segment1, segment2.start);
      if (s >= 0 && s <= 1) {
        if (ptEquals(segment1.position(s), segment2.start))
          result.push(
            new IntersectionPoint(segment1, s, segment2.start),
            new IntersectionPoint(segment2, 0, segment2.start)
          );
        else return null;
      }
    } else if (tb >= 1) {
      let s = nearestPoint(segment1, segment2.end);
      if (s >= 0 && s <= 1)
        if (ptEquals(segment1.position(s), segment2.end))
          result.push(
            new IntersectionPoint(segment1, s, segment2.end),
            new IntersectionPoint(segment2, 1, segment2.end)
          );
        else return null;
    } else {
      if (ptEquals(tp, segment2.position(tb)))
        result.push(
          new IntersectionPoint(segment1, i, tp),
          new IntersectionPoint(segment2, tb, tp)
        );
      else return null;
    }
  }
  return result;
}

function sub(pt1: Pt, pt2: Pt): Pt {
  return pt(pt1.x - pt2.x, pt1.y - pt2.y);
}

function cross(pt1: Pt, pt2: Pt): number {
  return pt1.x * pt2.y - pt1.y * pt2.x;
}

function dot(pt1: Pt, pt2: Pt): number {
  return pt1.x * pt2.x + pt1.y * pt2.y;
}

function nearestPoint(segment: Segment, p: Pt): number {
  let bSa = sub(segment.end, segment.start);
  let pSa = sub(p, segment.start);
  return dot(bSa, pSa) / dot(bSa, bSa);
}

export { IntersectionPoint, intersections };
